'use client';
import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import ProgressBar from '~/components/ProgressBar';

const GlobalPopupContext = createContext({
  showPopup: (options) => {},
});

const getIconChar = (type) => (type === 'success' ? '✓' : type === 'error' ? '!' : 'i');
const getIconBg = (type) =>
  type === 'success' ? '#e6f4ea' : type === 'error' ? '#B11211' : '#eceef0';
const getIconColor = (type) =>
  type === 'success' ? '#1e7e34' : type === 'error' ? '#fff' : '#495057';

const DEFAULT_DURATION = 4000;

const MAX_VISIBLE_STACK_DEPTH = 3;
const STACK_OFFSET_Y_PX = 32;
const STACK_OFFSET_X_PX = 32;
const STACK_SCALE_STEP = 0.035;
const STACK_OPACITY_STEP = 0.14;
const STACK_MIN_OPACITY = 0.55;

export default function GlobalPopupProvider({ children }) {
  const [popupQueue, setPopupQueue] = useState([]);
  const popupTimeouts = useRef(new Map());
  const popupHovered = useRef(false);

  const showPopup = useCallback(({ type = 'success', title = '', detail = '', duration = DEFAULT_DURATION }) => {
    const newPopup = {
      id: Date.now() + Math.random(),
      type,
      title,
      detail,
      duration,
      createdAt: Date.now(),
    };
    setPopupQueue((prev) => [...prev, newPopup]);
  }, []);

  const removeFromQueue = useCallback((id) => {
    setPopupQueue((prev) => prev.filter((popup) => popup.id !== id));
  }, []);

  const closePopup = useCallback((id) => {
    const timeout = popupTimeouts.current.get(id);
    if (timeout) clearTimeout(timeout);
    popupTimeouts.current.delete(id);
    popupHovered.current = false;
    setPopupQueue((prev) => {
      const index = prev.findIndex((p) => p.id === id);
      if (index !== -1) {
        const newQueue = [...prev];
        newQueue[index] = { ...newQueue[index], leaving: true };
        return newQueue;
      }
      return prev;
    });
  }, []);

  useEffect(() => {
    popupQueue.forEach((popup) => {
      if (!popupTimeouts.current.has(popup.id) && !popup.leaving && popup.duration > 0) {
        const timeout = setTimeout(() => closePopup(popup.id), popup.duration);
        popupTimeouts.current.set(popup.id, timeout);
      }
    });
    return () => {
      popupTimeouts.current.forEach((timeout) => clearTimeout(timeout));
      popupTimeouts.current.clear();
    };
  }, [popupQueue, closePopup]);

  return (
    <GlobalPopupContext.Provider value={{ showPopup }}>
      {children}

      {popupQueue.length > 0 && (
        <div className="global-popup-container">
          {popupQueue.map((popup, index) => {
            const distanceFromFront = popupQueue.length - 1 - index;
            const cappedDepth = Math.min(distanceFromFront, MAX_VISIBLE_STACK_DEPTH);
            const isFront = distanceFromFront === 0;
            const isHiddenBehind = distanceFromFront > MAX_VISIBLE_STACK_DEPTH;
            const translateX = -cappedDepth * STACK_OFFSET_X_PX;
            const translateY = -cappedDepth * STACK_OFFSET_Y_PX;
            const scale = 1 - cappedDepth * STACK_SCALE_STEP;

            return (
              <div
                key={popup.id}
                className="global-popup-slot"
                style={{
                  zIndex: popupQueue.length - distanceFromFront,
                  transform: `translate(${translateX}px, ${translateY}px) scale(${scale})`,
                  opacity: isHiddenBehind ? 0 : Math.max(STACK_MIN_OPACITY, 1 - cappedDepth * STACK_OPACITY_STEP),
                  pointerEvents: isFront ? 'auto' : 'none',
                }}
              >
                <div
                  className={`global-popup ${popup.leaving ? 'leaving' : ''}`}
                  onMouseEnter={() => { if (isFront) popupHovered.current = popup.id; }}
                  onMouseLeave={() => { popupHovered.current = false; }}
                  onAnimationEnd={(e) => {
                    if (popup.leaving && e.target === e.currentTarget) {
                      removeFromQueue(popup.id);
                    }
                  }}
                >
                  <button
                    type="button"
                    className="global-popup-close-btn"
                    onClick={() => closePopup(popup.id)}
                    aria-label="Meldung schließen"
                  >
                    ×
                  </button>

                  <div
                    className="global-popup-icon"
                    style={{
                      backgroundColor: getIconBg(popup.type),
                      color: getIconColor(popup.type),
                    }}
                  >
                    {getIconChar(popup.type)}
                  </div>
                  <div className="global-popup-text">
                    <div className="global-popup-title">{popup.title}</div>
                    {popup.detail && <div className="global-popup-detail">{popup.detail}</div>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ProgressBar />

      <style jsx global>{`
        @keyframes globalPopupSlideIn {
          0% { opacity: 0; transform: translateX(110%); }
          100% { opacity: 1; transform: translateX(0); }
        }
        @keyframes globalPopupSlideOut {
          0% { opacity: 1; transform: translateX(0); }
          100% { opacity: 0; transform: translateX(110%); }
        }
        .global-popup-container {
          position: fixed;
          bottom: 24px;
          right: 24px;
          width: min(420px, calc(100vw - 48px));
          z-index: 2147483647;
          pointer-events: none;
        }
        .global-popup-slot {
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          transform-origin: bottom right;
          transition: transform 0.25s ease, opacity 0.25s ease;
          will-change: transform, opacity;
        }
        .global-popup {
          position: relative;
          background: #ffffff;
          border-radius: 12px;
          padding: 18px;
          box-shadow: 0 14px 40px rgba(0, 0, 0, 0.18);
          border: 1px solid rgba(0, 0, 0, 0.08);
          display: flex;
          align-items: flex-start;
          gap: 12px;
          opacity: 0;
          transform: translateX(110%);
          animation: globalPopupSlideIn 0.4s ease-out forwards;
          pointer-events: auto;
          will-change: transform;
        }
        .global-popup.leaving {
          animation: globalPopupSlideOut 0.3s ease-in forwards !important;
          pointer-events: none;
        }
        .global-popup-close-btn {
          position: absolute;
          top: 8px;
          right: 8px;
          width: 24px;
          height: 24px;
          border: none;
          background: transparent;
          color: #999;
          font-size: 20px;
          line-height: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          border-radius: 6px;
          padding: 0;
          transition: background-color 0.15s ease, color 0.15s ease;
        }
        .global-popup-close-btn:hover {
          background-color: rgba(0, 0, 0, 0.06);
          color: #333;
        }
        .global-popup-icon {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          font-weight: 700;
          flex-shrink: 0;
        }
        .global-popup-text {
          flex: 1 1 auto;
          min-width: 0;
          padding-right: 18px;
        }
        .global-popup-title {
          font-weight: 700;
          font-size: 16px;
          color: #111;
          line-height: 1.2;
          margin-bottom: 6px;
        }
        .global-popup-detail {
          font-size: 13px;
          color: #555;
          line-height: 1.4;
          white-space: pre-line;
          word-break: break-word;
        }
      `}</style>
    </GlobalPopupContext.Provider>
  );
}

export const useGlobalPopup = () => {
  const context = useContext(GlobalPopupContext);
  if (!context) {
    throw new Error('useGlobalPopup muss innerhalb von GlobalPopupProvider verwendet werden');
  }
  return context;
};